import axios from 'axios'

export default {
  /**
   * Get setup options for the Campaign Setup Form.
   * Returns: countries, niches, campaign_types, platforms, creator_sizes, creator_counts, visibility_options
   *
   * @returns {Promise<{success: boolean, countries: Array, niches: Array, ...}>}
   */
  getSetupOptions: () => axios.get('/api/campaign-ai/setup-options'),

  /**
   * Start a new Campaign AI session.
   *
   * @returns {Promise<{success: boolean, session_id: string, task_id: string}>}
   */
  startSession: () => axios.post('/api/campaign-ai/start'),

  /**
   * Start a new Campaign AI session with initial setup data.
   *
   * @param {Object} initialData - Initial campaign setup data
   * @param {string} initialData.network - Platform ('instagram' or 'tiktok')
   * @param {string} initialData.campaign_type - Campaign type slug from database
   * @param {string} initialData.start_date - Campaign start date (YYYY-MM-DD)
   * @param {string} initialData.end_date - Campaign end date (YYYY-MM-DD)
   * @param {string} initialData.country - Country name
   * @param {string[]} initialData.niches - Selected niches
   * @param {string} initialData.number_of_creators - Number of creators range
   * @param {string} initialData.creator_size - Creator size ('nano', 'micro', 'macro')
   * @returns {Promise<{success: boolean, session_id: string, task_id: string}>}
   */
  startSessionWithData: (initialData) =>
    axios.post('/api/campaign-ai/start', { initial_data: initialData }),

  /**
   * Send a message to an existing session.
   *
   * @param {string} sessionId - The session ID
   * @param {string} message - The message to send
   * @param {Object} [options]
   * @param {boolean} [options.hidden=false] - When true, the message is persisted
   *   with hidden=true so it never renders in chat (used for silent
   *   form-submit context and brief-upload context messages)
   * @returns {Promise<{success: boolean, task_id: string}>}
   */
  sendMessage: (sessionId, message, options = {}) =>
    axios.post('/api/campaign-ai/message', {
      session_id: sessionId,
      message: message,
      ...(options.hidden ? { hidden: true } : {}),
    }),

  /**
   * Create a campaign using the existing campaign endpoint.
   * Accepts FormData with file upload support.
   *
   * @param {FormData} formData - Campaign data as FormData (includes file for cover image)
   * @returns {Promise<{message: string, data: Object}>}
   */
  createCampaign: (formData) =>
    axios.post('/api/campaign', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  /**
   * Create a brief for a campaign.
   *
   * @param {number} campaignId - The campaign ID
   * @param {Object} briefData - Brief data
   * @returns {Promise<Object>}
   */
  createBrief: (campaignId, briefData) => axios.post(`/api/campaign/${campaignId}/brief`, briefData),

  /**
   * Create a group for a campaign.
   *
   * @param {number} campaignId - The campaign ID
   * @param {Object} groupData - Group data
   * @returns {Promise<{message: string, data: Object}>}
   */
  createGroup: (campaignId, groupData) => axios.post(`/api/campaign/${campaignId}/group`, groupData),

  /**
   * Link a brief to a group.
   *
   * @param {number} campaignId - The campaign ID
   * @param {number} briefId - The brief ID
   * @param {number} groupId - The group ID
   * @returns {Promise<Object>}
   */
  linkBriefToGroup: (campaignId, briefId, groupId) =>
    axios.post(`/api/campaign/${campaignId}/links`, {
      brief_id: briefId,
      group_id: groupId,
    }),

  /**
   * Send brief to creators (opens campaign for applications).
   *
   * @param {number} campaignId - The campaign ID
   * @param {number} briefId - The brief ID
   * @param {number} groupId - The group ID
   * @returns {Promise<Object>}
   */
  sendBriefToCreators: (campaignId, briefId, groupId) =>
    axios.put(`/api/campaign/${campaignId}/links/${briefId}`, {
      group_id: groupId,
      brief_id: briefId,
      creators: [],
      brief_ids: [briefId],
      section: 'sendBriefToCreators',
    }),

  /**
   * Notify backend that entities have been created and continue AI flow.
   *
   * @param {string} sessionId - The session ID
   * @param {number} campaignId - The created campaign ID
   * @param {number} briefId - The created brief ID
   * @param {number} groupId - The created group ID
   * @returns {Promise<{success: boolean, task_id: string}>}
   */
  continueWithEntities: (sessionId, campaignId, briefId, groupId) =>
    axios.post('/api/campaign-ai/continue-with-entities', {
      session_id: sessionId,
      campaign_id: campaignId,
      brief_id: briefId,
      group_id: groupId,
    }),

  /**
   * Get cover image for display during confirm_cover step.
   * Does NOT delete the image from Redis.
   *
   * @param {string} sessionId - The session ID
   * @returns {Promise<{success: boolean, cover_image_base64: string}>}
   */
  getCoverImage: (sessionId) => axios.get(`/api/campaign-ai/session/${sessionId}/cover-image`),

  /**
   * Upload a custom cover image for the campaign.
   * Stores the image in Redis for later use during entity creation.
   *
   * @param {string} sessionId - The session ID
   * @param {string} base64Image - Base64 encoded image data
   * @returns {Promise<{success: boolean}>}
   */
  uploadCoverImage: (sessionId, base64Image) =>
    axios.post(`/api/campaign-ai/session/${sessionId}/cover-image`, {
      cover_image_base64: base64Image,
    }),

  /**
   * Get pending entity data for create_entities action.
   * Fetches full entity data including cover_image_base64 from Laravel.
   * Note: This deletes the cover image from Redis after fetching.
   *
   * @param {string} sessionId - The session ID
   * @returns {Promise<{success: boolean, campaign_data: Object, brief_data: Object, group_data: Object, cover_image_base64: string|null}>}
   */
  getPendingEntityData: (sessionId) => axios.get(`/api/campaign-ai/session/${sessionId}/pending-entities`),

  /**
   * Select influencers from AI recommendations.
   *
   * @param {string} sessionId - The session ID
   * @param {number[]} selectedIds - Array of selected influencer IDs
   * @param {boolean} selectAll - Whether to select all recommended influencers
   * @returns {Promise<{success: boolean, task_id: string}>}
   */
  selectInfluencers: (sessionId, selectedIds, selectAll = false, displayMessage = null) =>
    axios.post('/api/campaign-ai/select-influencers', {
      session_id: sessionId,
      selected_ids: selectedIds,
      select_all: selectAll,
      display_message: displayMessage,
    }),

  /**
   * Get session state by ID.
   *
   * @param {string} sessionId - The session ID
   * @returns {Promise<{success: boolean, session: Object}>}
   */
  getSession: (sessionId) => axios.get(`/api/campaign-ai/session/${sessionId}`),

  /**
   * Get user's session history.
   *
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Max number of sessions to return
   * @returns {Promise<{success: boolean, sessions: Array}>}
   */
  getSessions: (params) => axios.get('/api/campaign-ai/sessions', { params }),

  /**
   * Store creator recommendations on the last AI message in the session.
   * Persists basic creator data so cards survive page reloads.
   *
   * @param {string} sessionId - The session ID
   * @param {Array} creators - Basic creator data [{id, username, followers, score}]
   * @returns {Promise<{success: boolean}>}
   */
  storeMessageCreators: (sessionId, creators) =>
    axios.post(`/api/campaign-ai/session/${sessionId}/message-creators`, {
      creators: creators,
    }),

  /**
   * Get enriched creator details for AI recommendations.
   * Fetches avatar, location, engagement rate, and niches for a list of creator IDs.
   *
   * @param {number[]} creatorIds - Array of creator/profile IDs
   * @returns {Promise<{success: boolean, creators: Object}>} creators is keyed by ID
   */
  getCreatorDetails: (creatorIds) =>
    axios.post('/api/campaign-ai/creators/details', {
      creator_ids: creatorIds,
    }),


  /**
   * Select a cover image from the AI-generated gallery.
   *
   * @param {string} sessionId - The session ID
   * @param {number} imageId - The image ID to select
   * @returns {Promise<{success: boolean, image_id: number, image_url: string}>}
   */
  selectCoverImage: (sessionId, imageId) =>
    axios.post(`/api/campaign-ai/session/${sessionId}/select-image`, { image_id: imageId }),

  /**
   * Change the whitelabel app attached to an AI session (PD-4871).
   * The backend persists the choice immediately; the confirmation (and a
   * refreshed summary card when the graph sits at show_summary) arrives via
   * the private-campaign-ai.{user_id} websocket channel as action 'app_changed'.
   *
   * @param {string} sessionId - The session ID
   * @param {number} appId - The whitelabel app id (real id, from apps_list)
   * @returns {Promise<{success: boolean}>}
   */
  changeSessionApp: (sessionId, appId) =>
    axios.post(`/api/campaign-ai/session/${sessionId}/app`, { app_id: appId }),

  /**
   * Delete a session.
   *
   * @param {string} sessionId - The session ID
   * @returns {Promise<{success: boolean, message: string}>}
   */
  deleteSession: (sessionId) => axios.delete(`/api/campaign-ai/session/${sessionId}`),

  /**
   * Upload a brief file and extract campaign details.
   * The AI will process the file content and create a campaign summary.
   *
   * @param {File} file - The file to upload (TXT, PDF, DOC, DOCX, RTF, MD)
   * @param {string|null} sessionId - Optional session ID if already in a session
   * @returns {Promise<{success: boolean, extracted_text: string, campaign_summary: object}>}
   */
  uploadBriefFile: (file, sessionId = null) => {
    const formData = new FormData()
    formData.append('file', file)
    if (sessionId) {
      formData.append('session_id', sessionId)
    }
    return axios.post('/api/campaign-ai/upload-brief', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  /**
   * Store extracted brief data in Redis for a session.
   * Used when a brief was uploaded before the session existed.
   *
   * @param {string} sessionId - The session ID
   * @param {Object} briefData - The extracted campaign summary
   * @returns {Promise<{success: boolean}>}
   */
  storeBriefData: (sessionId, briefData) =>
    axios.post('/api/campaign-ai/store-brief', {
      session_id: sessionId,
      brief_data: briefData,
    }),

}
